import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { getStoredAuth, clearStoredAuth } from './authUtils';
import { LoginPage } from './LoginPage';
import { useTheme } from '../styles/ThemeContext';

/**
 * DataLoader handles authentication state and loads initial data
 * Shows LoginPage if not authenticated, otherwise loads data and renders children
 */
export function DataLoader({ children }) {
  const [auth, setAuth] = useState(() => getStoredAuth());
  const [loading, setLoading] = useState(!!auth);
  const [defaults, setDefaults] = useState({
    products: [],
    unitTypes: [],
    countries: [],
    guidelines: [],
    savedAddresses: [],
    customProducts: []
  });
  const [logoutCount, setLogoutCount] = useState(0);
  const { theme, applyUserTheme } = useTheme();
  const c = theme.colors;

  // Apply saved theme on mount (page refresh with existing session)
  useEffect(() => {
    if (auth?.user?.theme_preference) applyUserTheme(auth.user.theme_preference);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onLogin = (token, user) => {
    setAuth({ token, user });
    if (user?.theme_preference) applyUserTheme(user.theme_preference);
    setLoading(true);
  };

  const onLogout = async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // ignore - logout locally even if API fails
    }
    clearStoredAuth();
    setAuth(null);
    setLogoutCount(prev => prev + 1);
  };

  useEffect(() => {
    if (!auth) return;

    const load = (path) =>
      apiFetch(path)
        .then((r) => {
          if (r.status === 401) throw new Error('Unauthorized');
          return r.ok ? r.json() : [];
        })
        .catch((err) => {
          if (err.message === 'Unauthorized') throw err;
          return [];
        });

    Promise.allSettled([
      load('/api/products'),
      load('/api/shipping-units'),
      load('/api/countries'),
      load('/api/guidelines'),
      load('/api/addresses'),
      load('/api/custom-products')
    ])
      .then(([p, u, c, g, a, cp]) => {
        if (p.status === 'rejected' && p.reason?.message === 'Unauthorized') {
          onLogout();
          return;
        }

        setDefaults({
          products: p.status === 'fulfilled' && Array.isArray(p.value) ? p.value : [],
          unitTypes: u.status === 'fulfilled' && Array.isArray(u.value) ? u.value : [],
          countries: c.status === 'fulfilled' && Array.isArray(c.value) ? c.value : [],
          guidelines: g.status === 'fulfilled' && Array.isArray(g.value) ? g.value : [],
          savedAddresses: a.status === 'fulfilled' && Array.isArray(a.value) ? a.value : [],
          customProducts: cp.status === 'fulfilled' && Array.isArray(cp.value) ? cp.value : []
        });

        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [auth]);

  if (!auth) {
    return <LoginPage key={logoutCount} onSuccess={onLogin} />;
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: c.bg,
          color: c.text,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Segoe UI', sans-serif",
          fontSize: 16
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
          <div style={{ color: c.accent, fontWeight: 600 }}>Loading…</div>
        </div>
      </div>
    );
  }

  return children({
    user: auth.user,
    onLogout,
    defaults
  });
}
